import { Observable } from 'rxjs';

import {
  CardUpdateCheckout,
  ChargeVerification,
  CheckoutOutcome,
  PaymentMethodKind,
  SubscriptionCard,
  SubscriptionCycle,
  SubscriptionInvoice,
  SubscriptionCatalog,
  SubscriptionQuote,
  SubscriptionSnapshot,
} from '../models/subscription.model';

/**
 * Everything a paid transition needs. `planId` is resolved from the catalog by
 * the store before it gets here, so the port never has to guess one.
 */
export interface CheckoutRequest {
  readonly planId: number;
  readonly cycle: SubscriptionCycle;
  readonly promoCode?: string | null;
  readonly useWallet?: boolean;
  readonly cardId?: string | null;
  readonly paymentMethod?: PaymentMethodKind | null;
  /** Absolute URL the gateway should return the browser to. */
  readonly redirectUrl?: string | null;
}

/**
 * SubscriptionRepository — the DDD port for the subscription backend.
 *
 * Domain-typed on both sides: no wire DTO ever crosses it (mapping lives in
 * `infrastructure/subscription.mapper.ts`). {@link SubscriptionStore} is the only
 * consumer — it owns the state, the cache and the orchestration; implementations
 * here own transport only.
 *
 * Mutations that only ECHO what changed (`trial`, `cancel`, `auto-renew`) return
 * a PARTIAL snapshot on purpose: the store merges it
 * onto what it already holds, so a partial echo can never blank the plan/policy
 * a full read established.
 *
 * Bound at ROOT (not per route): subscription state gates UI across the whole
 * app, so a single instance must serve every surface. The mock/http binding
 * itself (config-driven) lives in `subscription.providers.ts`'s
 * `provideSubscriptionData()` — NOT here, so this domain-layer file never
 * imports infrastructure (see `tools/scripts/check-architecture.mjs`'s
 * layer-direction rule).
 */
export abstract class SubscriptionRepository {
  /** `GET /subscription` — the authoritative state, policy and today's usage. */
  abstract getSnapshot(): Observable<SubscriptionSnapshot>;

  /** `GET /subscription/plans` — the PUBLIC subscribable catalog. */
  abstract plans(): Observable<SubscriptionCatalog>;

  /** `POST /trial` — the one-time free trial. Rejects (422) when not allowed. */
  abstract startTrial(): Observable<Partial<SubscriptionSnapshot>>;

  /** `POST /cancel` — → `cancelled_grace`. `reason` is the optional exit survey. */
  abstract cancel(reason?: string | null): Observable<Partial<SubscriptionSnapshot>>;

  /** `POST /subscribe` — start paying. Resolves to where the user goes next. */
  abstract subscribe(request: CheckoutRequest): Observable<CheckoutOutcome>;

  /** `POST /reactivate` — resume from grace / post-cancellation. */
  abstract reactivate(request: CheckoutRequest): Observable<CheckoutOutcome>;

  /** `POST /quote` — wallet + promo breakdown for a plan, before committing. */
  abstract quote(request: CheckoutRequest): Observable<SubscriptionQuote>;

  /** `POST /auto-renew` — flip automatic renewal. */
  abstract setAutoRenew(on: boolean): Observable<Partial<SubscriptionSnapshot>>;

  /**
   * `POST /payment-method` — «تغيير البطاقة»: open a captured, refunded
   * verification charge that stores the new card as the renewal mandate
   * (SUBSCRIPTION-RECURRING R3). Resolves to where to send the user, never to a
   * changed card — that only lands once the charge captures.
   */
  abstract updatePaymentMethod(redirectUrl: string | null): Observable<CardUpdateCheckout>;

  /** `POST /payments/tap/verify` — fast-path confirmation of a returned charge (idempotent). */
  abstract verifyCharge(chargeId: string): Observable<ChargeVerification>;

  /** `GET /invoices` — billing history, newest first. */
  abstract invoices(): Observable<SubscriptionInvoice[]>;

  /** `GET /cards` — saved payment cards, default first. */
  abstract cards(): Observable<SubscriptionCard[]>;

  /** `GET /tools` — how many Khoyek tools the catalog holds (the S1 «الأدوات» row); null when unknown. */
  abstract toolsCount(): Observable<number | null>;
}
